import { ToolType } from '@bizzdesign/bizzdesign-sdk';
import {
  type FrameItem,
  isWorkspaceFrameItem,
} from '@bizzdesign/data-access-workspaces';
import {
  ArrangeKeys,
  type BDMenuItem,
  DisplayMenuKeys,
  EditMenuKeys,
  FrameMenuModelerKeys,
  ModelingAssistanceKeys,
  ToolsMenuKeys,
  WorkspaceActionKeys,
} from '@bizzdesign/shared/models';
import { type BehaviorSubject } from 'rxjs';

type EditMenuCommandCallback = (key: string) => (() => void) | undefined;

type MenuItemGeneratorCallback = (frame: FrameItem) => BDMenuItem[];

export const isDesignToolType = (frame: FrameItem) =>
  frame.toolType === ToolType.DESIGN;

export const resolveCallbackForDesignType = (
  frame: FrameItem,
  callback: MenuItemGeneratorCallback,
): BDMenuItem[] => {
  if (!isDesignToolType(frame)) return [];

  return callback(frame);
};

export const toolsMenuItems = (): BDMenuItem[] => [
  {
    labelKey: ToolsMenuKeys.Tools,
    tScope: 'uiComponents',
    items: [
      {
        id: `modeler-${ToolsMenuKeys.Text}`,
        labelKey: ToolsMenuKeys.Text,
        tScope: 'featureModeler',
        shortcut: { key: 'T', modifierKey: false, shiftKey: false },
      },
      {
        id: `modeler-${ToolsMenuKeys.Sticky}`,
        labelKey: ToolsMenuKeys.Sticky,
        tScope: 'featureModeler',
        shortcut: { key: 'S', modifierKey: false, shiftKey: false },
      },
      {
        id: `modeler-${ToolsMenuKeys.Shape}`,
        labelKey: ToolsMenuKeys.Shape,
        tScope: 'featureModeler',
      },
      { separator: true },
      {
        id: `modeler-${ToolsMenuKeys.CoreObjects}`,
        labelKey: ToolsMenuKeys.CoreObjects,
        tScope: 'featureModeler',
        shortcut: { key: 'C', modifierKey: false, shiftKey: false },
      },
      {
        id: `modeler-${ToolsMenuKeys.RepositoryObjects}`,
        labelKey: ToolsMenuKeys.RepositoryObjects,
        tScope: 'featureModeler',
        shortcut: { key: 'R', modifierKey: false, shiftKey: true },
      },
      { separator: true },
      {
        id: `modeler-${ToolsMenuKeys.Comment}`,
        labelKey: ToolsMenuKeys.Comment,
        tScope: 'featureModeler',
        subject: 'commentThreads',
        actionKey: 'view',
      },
    ],
  },
];

export const arrangeMenuItems = (
  getKeyFromCode: (code: string, fallback: string) => string,
): BDMenuItem[] => [
  {
    labelKey: ArrangeKeys.Arrange,
    tScope: 'uiComponents',
    items: [
      {
        id: `modeler-${ArrangeKeys.Group}`,
        labelKey: ArrangeKeys.Group,
        tScope: 'featureModeler',
        shortcut: {
          key: 'G',
          modifierKey: true,
          shiftKey: false,
        },
      },
      {
        id: `modeler-${ArrangeKeys.Ungroup}`,
        labelKey: ArrangeKeys.Ungroup,
        tScope: 'featureModeler',
        shortcut: {
          key: 'G',
          modifierKey: true,
          shiftKey: true,
        },
      },
      { separator: true },
      {
        id: `modeler-${ArrangeKeys.BringForward}`,
        labelKey: ArrangeKeys.BringForward,
        tScope: 'featureModeler',
        shortcut: {
          key: getKeyFromCode('BracketRight', ']'),
          modifierKey: true,
          shiftKey: false,
        },
      },
      {
        id: `modeler-${ArrangeKeys.BringToFront}`,
        labelKey: ArrangeKeys.BringToFront,
        tScope: 'featureModeler',
        shortcut: {
          key: getKeyFromCode('BracketRight', ']'),
          modifierKey: false,
          shiftKey: false,
        },
      },
      {
        id: `modeler-${ArrangeKeys.SendBackward}`,
        labelKey: ArrangeKeys.SendBackward,
        tScope: 'featureModeler',
        shortcut: {
          key: getKeyFromCode('BracketLeft', '['),
          modifierKey: true,
          shiftKey: false,
        },
      },
      {
        id: `modeler-${ArrangeKeys.SendToBack}`,
        labelKey: ArrangeKeys.SendToBack,
        tScope: 'featureModeler',
        shortcut: {
          key: getKeyFromCode('BracketLeft', '['),
          modifierKey: false,
          shiftKey: false,
        },
      },
    ],
  },
];

export const displayMenuItems = (): BDMenuItem[] => [
  {
    labelKey: DisplayMenuKeys.Display,
    tScope: 'uiComponents',
    items: [
      {
        id: `modeler-${DisplayMenuKeys.Fullscreen}`,
        labelKey: DisplayMenuKeys.Fullscreen,
        tScope: 'uiComponents',
        shortcut: { key: 'F11', modifierKey: false, shiftKey: false },
      },
      {
        id: `modeler-${DisplayMenuKeys.FitToScreen}`,
        labelKey: DisplayMenuKeys.FitToScreen,
        tScope: 'uiComponents',
        shortcut: {
          key: '1',
          modifierKey: false,
          shiftKey: false,
          altKey: true,
        },
      },
      { separator: true },
      {
        id: `modeler-${DisplayMenuKeys.StickyAuthors}`,
        labelKey: DisplayMenuKeys.StickyAuthors,
        tScope: 'uiComponents',
        items: [
          {
            id: `modeler-${DisplayMenuKeys.StickyAuthorsShowAll}`,
            labelKey: DisplayMenuKeys.StickyAuthorsShowAll,
            tScope: 'uiComponents',
          },
          {
            id: `modeler-${DisplayMenuKeys.StickyAuthorsHideAll}`,
            labelKey: DisplayMenuKeys.StickyAuthorsHideAll,
            tScope: 'uiComponents',
          },
        ],
      },
      {
        id: `modeler-${DisplayMenuKeys.Sidepanel}`,
        labelKey: DisplayMenuKeys.Sidepanel,
        tScope: 'uiComponents',
        items: [
          {
            id: `modeler-${DisplayMenuKeys.SidepanelOff}`,
            labelKey: DisplayMenuKeys.SidepanelOff,
            tScope: 'uiComponents',
          },
          {
            id: `modeler-${DisplayMenuKeys.ModelingAssistance}`,
            labelKey: DisplayMenuKeys.ModelingAssistance,
            tScope: 'uiComponents',
          },
        ],
      },
    ],
  },
];

export const displayMenuItemsPublishedView = (): BDMenuItem[] => [
  {
    labelKey: DisplayMenuKeys.Display,
    tScope: 'uiComponents',
    items: [
      {
        labelKey: DisplayMenuKeys.Fullscreen,
        tScope: 'uiComponents',
        shortcut: { key: 'F11', modifierKey: false, shiftKey: false },
      },
      {
        labelKey: DisplayMenuKeys.FitToScreen,
        tScope: 'uiComponents',
        shortcut: {
          key: '1',
          modifierKey: false,
          shiftKey: false,
          altKey: true,
        },
      },
    ],
  },
];

export const frameMenuItems = (
  frame: FrameItem,
  pinUnpinLabelKey: BehaviorSubject<string>,
): BDMenuItem[] => {
  if (!isDesignToolType(frame)) {
    return [
      {
        labelKey: 'menu.renameFrame',
        tScope: 'uiComponents',
      },
      {
        separator: true,
      },
      {
        labelKey: 'workspace.inviteButtonText',
        tScope: 'featureWorkspaces',
      },
      {
        labelKey: 'menu.duplicate',
        tScope: 'uiComponents',
        disabled: !(frame && isWorkspaceFrameItem(frame)),
      },
      {
        separator: true,
      },
      {
        id: 'pinUnpinFrame',
        labelKey$: pinUnpinLabelKey,
        tScope: 'uiComponents',
      },
      {
        separator: true,
      },
      {
        labelKey: 'menu.deleteFrame',
        tScope: 'uiComponents',
      },
    ];
  }
  return [
    {
      labelKey: 'menu.renameFrame',
      tScope: 'uiComponents',
    },
    {
      separator: true,
    },
    {
      id: `modeler-${FrameMenuModelerKeys.PublishToRepo}`,
      labelKey: FrameMenuModelerKeys.PublishToRepo,
      tScope: 'uiComponents',
      state: {
        origin: 'modeler',
      },
    },
    {
      labelKey: 'workspace.inviteButtonText',
      tScope: 'featureWorkspaces',
    },
    {
      separator: true,
    },
    {
      id: `modeler-${FrameMenuModelerKeys.Autosave}`,
      labelKey: FrameMenuModelerKeys.Autosave,
      tScope: 'uiComponents',
      state: {
        origin: 'modeler',
        actionKey: 'autosave',
      },
      disabled: !(frame && isWorkspaceFrameItem(frame)),
      shortcut: { key: 'S', modifierKey: true, shiftKey: false },
    },
    {
      labelKey: 'menu.duplicate',
      tScope: 'uiComponents',
      disabled: !(frame && isWorkspaceFrameItem(frame)),
    },
    {
      separator: true,
    },
    {
      id: 'pinUnpinFrame',
      labelKey$: pinUnpinLabelKey,
      tScope: 'uiComponents',
    },
    {
      separator: true,
    },
    {
      labelKey: 'menu.deleteFrame',
      tScope: 'uiComponents',
    },
  ];
};

export const editMenuItems = (
  commandCallback?: EditMenuCommandCallback,
): BDMenuItem[] => [
  {
    labelKey: EditMenuKeys.Edit,
    tScope: 'uiComponents',
    items: [
      {
        id: `modeler-${EditMenuKeys.Undo}`,
        labelKey: EditMenuKeys.Undo,
        tScope: 'uiComponents',
        shortcut: { key: 'Z', modifierKey: true, shiftKey: false },
      },
      {
        id: `modeler-${EditMenuKeys.Redo}`,
        labelKey: EditMenuKeys.Redo,
        tScope: 'uiComponents',
        shortcut: { key: 'Z', modifierKey: true, shiftKey: true },
      },
      { separator: true },
      {
        id: `modeler-${EditMenuKeys.Copy}`,
        labelKey: EditMenuKeys.Copy,
        tScope: 'uiComponents',
        shortcut: { key: 'C', modifierKey: true, shiftKey: false },
      },
      {
        id: `modeler-${WorkspaceActionKeys.AddTo}`,
        labelKey: WorkspaceActionKeys.AddTo,
        tScope: 'uiComponents',
        command: commandCallback?.(WorkspaceActionKeys.AddTo),
        tooltipKey: 'menu.addTo.tooltipDisabled',
        shortcut: { key: 'C', modifierKey: true, shiftKey: true },
      },
      {
        id: `modeler-${EditMenuKeys.Duplicate}`,
        labelKey: EditMenuKeys.Duplicate,
        tScope: 'uiComponents',
        shortcut: { key: 'D', modifierKey: true, shiftKey: false },
      },
      {
        id: `modeler-${EditMenuKeys.Paste}`,
        labelKey: EditMenuKeys.Paste,
        tScope: 'uiComponents',
        shortcut: { key: 'V', modifierKey: true, shiftKey: false },
      },
      { separator: true },
      {
        id: `modeler-${EditMenuKeys.SelectAll}`,
        labelKey: EditMenuKeys.SelectAll,
        tScope: 'uiComponents',
        shortcut: { key: 'A', modifierKey: true, shiftKey: false },
      },
      {
        id: `modeler-${EditMenuKeys.SelectSimilar}`,
        labelKey: EditMenuKeys.SelectSimilar,
        tScope: 'uiComponents',
        shortcut: { key: 'E', modifierKey: true, shiftKey: false },
      },
    ],
  },
];

export const modelingAssistanceMenuItems = (): BDMenuItem[] => [
  {
    labelKey: 'menu.modelingAssistance',
    tScope: 'uiComponents',
    items: [
      {
        id: `modeler-${ModelingAssistanceKeys.ModelingAssistanceOff}`,
        labelKey: 'menu.modelingAssistanceOff',
        tScope: 'uiComponents',
      },
      {
        id: `modeler-${ModelingAssistanceKeys.Core}`,
        labelKey: 'subMenu.coreMetamodelTab',
        tScope: 'featureModeler',
      },
    ],
  },
];
